import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Sparkles, X } from 'lucide-react';

const SearchBar = ({ setSearch, search }) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
        <div className="relative max-w-xl mx-auto my-8 px-4">
            {/* Animated Background Glow */}
            <AnimatePresence>
                {isFocused && (
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="absolute -inset-4 bg-gradient-to-r from-red-600/20 via-green-500/20 to-red-600/20 blur-3xl rounded-full -z-10"
                    />
                )}
            </AnimatePresence>

            <motion.div 
                animate={{ 
                    scale: isFocused ? 1.02 : 1,
                    borderColor: isFocused ? 'rgba(239, 68, 68, 0.5)' : 'rgba(255, 255, 255, 0.1)'
                }}
                className="relative flex items-center bg-white/5 dark:bg-black/40 backdrop-blur-2xl border-2 rounded-full px-6 py-3 shadow-2xl transition-all"
            >
                {/* Slim Search Icon */}
                <Search 
                    size={20} 
                    className={`transition-colors duration-300 ${isFocused ? 'text-red-500' : 'text-gray-500'}`} 
                />

                <input 
                    type="text" 
                    value={search}
                    placeholder="Quick search tech..."
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    className="flex-1 bg-transparent border-none outline-none px-4 text-sm md:text-base font-medium placeholder:text-gray-500 tracking-tight"
                    onChange={(e) => setSearch(e.target.value)}
                />

                {/* Right Side Interaction */}
                <div className="flex items-center gap-3">
                    {search.length > 0 && (
                        <motion.button
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            onClick={() => setSearch('')}
                            className="p-1 hover:bg-white/10 rounded-full text-gray-500"
                        >
                            <X size={16} />
                        </motion.button>
                    )}
                    
                    <div className="h-6 w-[1px] bg-white/10 mx-1" />
                    
                    <motion.div
                        animate={{ rotate: isFocused ? [0, 15, -15, 0] : 0 }}
                        transition={{ repeat: isFocused ? Infinity : 0, duration: 2 }}
                    >
                        <Sparkles 
                            size={18} 
                            className={isFocused ? "text-green-500" : "text-gray-600"} 
                        />
                    </motion.div>
                </div>

                {/* Slim "Liquid" Border Shimmer */}
                {isFocused && (
                    <motion.div 
                        layoutId="shimmer"
                        className="absolute inset-0 rounded-full border border-red-500/50 pointer-events-none"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    />
                )}
            </motion.div>

            {/* Micro-Interaction: Result Count */}
            <AnimatePresence>
                {search.length > 2 && (
                    <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 5 }}
                        className="absolute left-8 top-full text-[10px] font-bold uppercase tracking-widest text-green-500/70"
                    >
                        Searching...
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SearchBar;